import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Box from '@material-ui/core/Box';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import CustomizedSnackbars from '../_commons/CustomizedSnackbars.jsx';
import FullScreenLoadingProgress from '../_commons/FullScreenLoadingProgressComponent/FullScreenLoadingProgress';
import Copyright from '../SignInPage/Copyright';
import { PasswordResetService } from '../../_services/PasswordResetService';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
    position: 'relative'
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const ErrorMessagesText = {
  EMAIL_REQUIRED: "Please enter your email address.",
  EMAIL_INVALID: "Please enter a valid email address.",
  SERVER_ERROR: "An error occurred while processing your request. Please try again.",
};

const SuccessMessagesText = {
  EMAIL_SENT: "If an account with that email exists, a password reset link has been sent. Please check your email.",
};

export default function ForgotPassword() {
  const classes = useStyles();
  let history = useHistory();

  const [Email, setEmail] = useState("");
  const [WaitingResult, setWaitingResult] = useState(false);
  const [ErrorMessages, setErrorMessages] = useState<string[]>([]);
  const [SuccessMessages, setSuccessMessages] = useState<string[]>([]);

  useEffect(() => {
    if (emailInputRef && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  /**
   * Operation on Email Input TextField
   */
  const emailInputRef = useRef<HTMLInputElement>(null);
  const EmailFocusHandler = () => {
    if (emailInputRef && emailInputRef.current)
      emailInputRef.current.setSelectionRange(0, emailInputRef.current.value.length);
  };
  const EmailChangeHandler = (event: any) => {
    setEmail(event.target.value);
  };
  const EmailKeyDownHandler = (event: any) => {
    if (event.key === 'Enter') {
      RequestResetSubmitHandler(event);
    }
  };

  /**
   * Email validation
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Operation on Request Reset Submit
   */
  const RequestResetSubmitHandler = (e: any) => {
    e.preventDefault();

    if (!Email || Email.trim() === "") {
      RequestResetFailedHandler(ErrorMessagesText.EMAIL_REQUIRED);
      return;
    }

    if (!isValidEmail(Email)) {
      RequestResetFailedHandler(ErrorMessagesText.EMAIL_INVALID);
      return;
    }

    setWaitingResult(true);
    PasswordResetService.RequestPasswordReset(Email)
      .then(async (resp) => {
        if (resp.status === 200) {
          RequestResetSuccessHandler();
        } else {
          const errorText = await resp.text();
          RequestResetFailedHandler(errorText || ErrorMessagesText.SERVER_ERROR);
        }
      })
      .catch((error) => {
        RequestResetFailedHandler(ErrorMessagesText.SERVER_ERROR);
      });
  };

  const RequestResetSuccessHandler = () => {
    setSuccessMessages(SuccessMessages => [...SuccessMessages, SuccessMessagesText.EMAIL_SENT]);
    setWaitingResult(false);
    setEmail("");
  };

  const RequestResetFailedHandler = (error: string) => {
    setErrorMessages(ErrorMessages => [...ErrorMessages, error]);
    setWaitingResult(false);
  };

  const BackToSignInHandler = () => {
    history.push('/SignIn');
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar} title={`RADACAD Version${process.env.REACT_APP_VERSION_NUMBER ?? "??"}`}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Forgot Password
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: '16px' }}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        <form className={classes.form} noValidate>
          {WaitingResult ? (
            <FullScreenLoadingProgress style={{ position: 'absolute', height: '100%', width: '100%', zIndex: 999 }}></FullScreenLoadingProgress>
          ) : (
            <></>
          )}
          <div style={WaitingResult ? { opacity: '0.1' } : {}}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              inputRef={emailInputRef}
              onFocus={EmailFocusHandler}
              onChange={EmailChangeHandler}
              onKeyDown={EmailKeyDownHandler}
              className={"myThemeBorder"}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={RequestResetSubmitHandler}
            >
              Send Reset Link
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={BackToSignInHandler}
            >
              Back to Sign In
            </Button>
          </div>
        </form>
      </div>

      {ErrorMessages !== null &&
        ErrorMessages.map((errorMessage, index) => (
          <CustomizedSnackbars key={index} message={errorMessage} severity="error"></CustomizedSnackbars>
        ))}

      {SuccessMessages !== null &&
        SuccessMessages.map((successMessage, index) => (
          <CustomizedSnackbars key={index} message={successMessage} severity="success"></CustomizedSnackbars>
        ))}

      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  );
}
